import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import './NavBox.css'
import Projects from '../Projects/Projects.jsx'
import ContactMe from '../ContactMe/ContactMe'

// One layout container ("shell") holds both the navigation and the open panel,
// so they're positioned together with a consistent gap and never overlap.
//
// A panel can be closed by:
// - its close (×) button
// - the Escape key
// - clicking the 3D background outside the panel and nav
// - the browser/phone Back action (each open panel adds a history entry)
// - tapping its nav item again

const HammerIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" className="bi bi-hammer" aria-hidden="true" viewBox="0 0 16 16">
                        <path d="M9.972 2.508a.5.5 0 0 0-.16-.556l-.178-.129a5.009 5.009 0 0 0-2.076-.783C6.215.862 4.504 1.229 2.84 3.133H1.786a.5.5 0 0 0-.354.147L.146 4.567a.5.5 0 0 0 0 .706l2.571 2.579a.5.5 0 0 0 .708 0l1.286-1.29a.5.5 0 0 0 .146-.353V5.57l8.387 8.873A.5.5 0 0 0 14 14.5l1.5-1.5a.5.5 0 0 0 .017-.689l-9.129-8.63c.747-.456 1.772-.839 3.112-.839a.5.5 0 0 0 .472-.334z"/>
                        </svg>
)
const ContactIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" className="bi bi-person-lines-fill" aria-hidden="true" viewBox="0 0 16 16">
                            <path d="M6 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-5 6s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H1zM11 3.5a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 0 1h-4a.5.5 0 0 1-.5-.5zm.5 2.5a.5.5 0 0 0 0 1h4a.5.5 0 0 0 0-1h-4zm2 3a.5.5 0 0 0 0 1h2a.5.5 0 0 0 0-1h-2zm0 3a.5.5 0 0 0 0 1h2a.5.5 0 0 0 0-1h-2z"/>
                        </svg>
)

const SECTIONS = [
    { id: 'projects', label: 'Software Projects', title: 'Software Projects', Icon: HammerIcon, Content: Projects },
    { id: 'contact', label: 'Contact Me', title: 'Contact Me', Icon: ContactIcon, Content: ContactMe },
]

const NavBox = () => {
    const [active, setActive] = useState(null)
    const panelRef = useRef(null)
    const navRef = useRef(null)
    const buttonRefs = useRef({})
    const lastOpened = useRef(null)

    // FLIP animation for the nav icons/labels: when a panel opens or closes the
    // buttons switch between "icon beside label" and "icon above label". We
    // record where each icon/label is just before the switch (First), let the
    // new layout render (Last), then animate from the old spot to the new one
    // (Invert + Play) so they glide instead of jumping.
    const flipFrom = useRef(null)
    const snapshotNav = () => {
        if (!navRef.current) return
        const parts = navRef.current.querySelectorAll('.section svg, .section samp')
        flipFrom.current = new Map([...parts].map((el) => [el, el.getBoundingClientRect()]))
    }

    const open = (id) => {
        if (!active) snapshotNav() // only when the layout is about to change
        lastOpened.current = id
        if (window.history.state && window.history.state.panel) {
            window.history.replaceState({ panel: id }, '')
        } else {
            window.history.pushState({ panel: id }, '')
        }
        setActive(id)
    }

    const close = useCallback(() => {
        // Closing goes "back" so the phone's Back gesture and our buttons agree
        if (window.history.state && window.history.state.panel) window.history.back()
        else {
            snapshotNav()
            setActive(null)
        }
    }, [])

    const toggle = (id) => (active === id ? close() : open(id))

    // Back / forward buttons and gestures
    useEffect(() => {
        // A reload can leave a stale panel entry behind; start clean
        if (window.history.state && window.history.state.panel) window.history.replaceState(null, '')
        const onPopState = (event) => {
            snapshotNav()
            setActive(event.state && event.state.panel ? event.state.panel : null)
        }
        window.addEventListener('popstate', onPopState)
        return () => window.removeEventListener('popstate', onPopState)
    }, [])

    // Escape key and clicks on the background close the panel
    useEffect(() => {
        if (!active) return undefined
        const onKeyDown = (event) => {
            if (event.key === 'Escape') close()
        }
        const onClick = (event) => {
            const target = event.target
            if (panelRef.current && panelRef.current.contains(target)) return
            if (navRef.current && navRef.current.contains(target)) return
            if (target.closest && target.closest('.tilt-hint')) return
            close()
        }
        window.addEventListener('keydown', onKeyDown)
        document.addEventListener('click', onClick)
        return () => {
            window.removeEventListener('keydown', onKeyDown)
            document.removeEventListener('click', onClick)
        }
    }, [active, close])

    // Focus management + a body class CSS can use (e.g. to hide the tilt hint)
    useEffect(() => {
        document.body.classList.toggle('panel-open', Boolean(active))
        window.dispatchEvent(new CustomEvent('panelchange', { detail: { open: Boolean(active), id: active } }))
        if (active) {
            if (panelRef.current) panelRef.current.focus({ preventScroll: true })
        } else if (lastOpened.current && buttonRefs.current[lastOpened.current]) {
            buttonRefs.current[lastOpened.current].focus({ preventScroll: true })
        }
    }, [active])

    const hasPanel = Boolean(active)
    useLayoutEffect(() => {
        const from = flipFrom.current
        flipFrom.current = null
        if (!from || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
        from.forEach((first, el) => {
            if (!el.isConnected) return
            const last = el.getBoundingClientRect()
            const dx = first.left - last.left
            const dy = first.top - last.top
            if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return
            el.animate(
                [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }],
                { duration: 360, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' }
            )
        })
    }, [hasPanel])

    const current = SECTIONS.find((section) => section.id === active)

    return (
        <div className={`shell${current ? ' has-panel' : ''}`}>
            {current && (
                <section
                    key={current.id}
                    id={`panel-${current.id}`}
                    className={`panel panel--${current.id}`}
                    aria-labelledby={`panel-title-${current.id}`}
                    tabIndex={-1}
                    ref={panelRef}
                >
                    <header className="panel__header">
                        <h1 id={`panel-title-${current.id}`}>
                            <samp>{current.title}</samp>
                        </h1>
                        <button
                            type="button"
                            className="panel__close"
                            onClick={close}
                            aria-label={`Close ${current.title}`}
                        >
                            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" fill="none" />
                            </svg>
                        </button>
                    </header>
                    <div className="panel__body">
                        <current.Content />
                    </div>
                </section>
            )}

            <nav className="nav" aria-label="Main" ref={navRef}>
                {SECTIONS.map(({ id, label, Icon }) => (
                    <button
                        key={id}
                        type="button"
                        ref={(el) => (buttonRefs.current[id] = el)}
                        className={`section${active === id ? ' is-active' : ''}`}
                        aria-expanded={active === id}
                        aria-controls={`panel-${id}`}
                        onClick={() => toggle(id)}
                    >
                        <Icon />
                        <samp>{label}</samp>
                    </button>
                ))}
            </nav>
        </div>
    )
}

export default NavBox
